/**
 * Category Filter - Hierarchical checkbox tree for log categories
 *
 * Categories are dot-separated (e.g., "a8.qubes.main.ActiveDirectoryUserImport")
 * and displayed as a tree with parent/child relationships.
 *
 * Selecting/deselecting a parent applies to all children.
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";

// Tree node representing a category or category segment
interface CategoryNode {
  name: string;
  fullPath: string;
  children: Map<string, CategoryNode>;
  isLeaf: boolean;
}

export interface CategoryFilterProps {
  categories: string[]; // Available categories from messages
  selectedCategories: Set<string>;
  onSelectionChange: (selected: Set<string>) => void;
  allSelected: boolean;
  onAllSelectedChange: (allSelected: boolean) => void;
}

export function CategoryFilter(props: CategoryFilterProps) {
  const { categories, selectedCategories, onSelectionChange, allSelected, onAllSelectedChange } = props;
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Build tree structure from flat category list
  const categoryTree = useMemo(() => {
    const root = new Map<string, CategoryNode>();

    categories.forEach(category => {
      if (!category || !category.trim()) return;

      const parts = category.split('.');
      let currentLevel = root;

      parts.forEach((part, index) => {
        const fullPath = parts.slice(0, index + 1).join('.');

        if (!currentLevel.has(part)) {
          currentLevel.set(part, {
            name: part,
            fullPath,
            children: new Map(),
            isLeaf: index === parts.length - 1,
          });
        }

        const node = currentLevel.get(part)!;
        if (index === parts.length - 1) {
          node.isLeaf = true;
        }
        currentLevel = node.children;
      });
    });

    return root;
  }, [categories]);

  // Get all descendants of a category path
  const getDescendants = useCallback((path: string): string[] => {
    const descendants: string[] = [];

    categories.forEach(cat => {
      if (cat.startsWith(path + '.') || cat === path) {
        descendants.push(cat);
      }
    });

    return descendants;
  }, [categories]);

  // Check if a category or any of its descendants are selected
  const isNodeSelected = useCallback((fullPath: string, node: CategoryNode): boolean => {
    // If this exact path is selected, return true
    if (selectedCategories.has(fullPath)) return true;

    // If this is a leaf, we're done
    if (node.isLeaf) return false;

    // Check if all descendants are selected
    const descendants = getDescendants(fullPath);
    if (descendants.length === 0) return false;

    return descendants.every(d => selectedCategories.has(d));
  }, [selectedCategories, getDescendants]);

  // Check if a category is partially selected (some but not all descendants)
  const isNodePartiallySelected = useCallback((fullPath: string, node: CategoryNode): boolean => {
    // Leaves can't be partially selected
    if (node.isLeaf) return false;

    const descendants = getDescendants(fullPath);
    if (descendants.length === 0) return false;

    const selectedDescendants = descendants.filter(d => selectedCategories.has(d));
    return selectedDescendants.length > 0 && selectedDescendants.length < descendants.length;
  }, [selectedCategories, getDescendants]);

  // Toggle a category (and all its descendants)
  const handleToggle = useCallback((fullPath: string, node: CategoryNode) => {
    const newSelected = new Set(selectedCategories);
    const descendants = getDescendants(fullPath);

    // If currently selected (fully), deselect this and all descendants
    if (isNodeSelected(fullPath, node)) {
      descendants.forEach(d => newSelected.delete(d));
      newSelected.delete(fullPath);
      onAllSelectedChange(false);
    } else {
      // Select this and all descendants
      descendants.forEach(d => newSelected.add(d));
      if (descendants.length > 0 || node.isLeaf) {
        // Only add non-leaf nodes if they have descendants, or if it's a leaf
        descendants.forEach(d => newSelected.add(d));
      }
      // Check if we now have everything
      if (newSelected.size === categories.length) {
        onAllSelectedChange(true);
      }
    }

    onSelectionChange(newSelected);
  }, [selectedCategories, onSelectionChange, onAllSelectedChange, getDescendants, isNodeSelected, categories]);

  const handleSelectAll = useCallback(() => {
    onSelectionChange(new Set(categories));
    onAllSelectedChange(true);
  }, [categories, onSelectionChange, onAllSelectedChange]);

  const handleClearAll = useCallback(() => {
    onSelectionChange(new Set());
    onAllSelectedChange(false);
  }, [onSelectionChange, onAllSelectedChange]);

  // Track expanded state for each node (start with everything collapsed)
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const toggleExpanded = useCallback((fullPath: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(fullPath)) {
        next.delete(fullPath);
      } else {
        next.add(fullPath);
      }
      return next;
    });
  }, []);

  // Render a tree node with its children
  const renderNode = useCallback((node: CategoryNode, depth: number = 0): React.ReactNode => {
    const hasChildren = node.children.size > 0;
    const isSelected = isNodeSelected(node.fullPath, node);
    const isPartial = isNodePartiallySelected(node.fullPath, node);
    const isExpanded = expandedNodes.has(node.fullPath);

    return (
      <div key={node.fullPath} className="category-filter__node">
        <label
          className="category-filter__item"
          style={{ paddingLeft: `${depth * 1.5}rem` }}
        >
          {hasChildren ? (
            <span
              className={`category-filter__expander ${isExpanded ? 'category-filter__expander--expanded' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleExpanded(node.fullPath);
              }}
            >
              ▸
            </span>
          ) : (
            <span className="category-filter__expander category-filter__expander--spacer" />
          )}
          <input
            type="checkbox"
            checked={isSelected}
            ref={input => {
              if (input) {
                input.indeterminate = isPartial;
              }
            }}
            onChange={() => handleToggle(node.fullPath, node)}
          />
          <span className="category-filter__label" title={node.fullPath}>
            {node.name}
          </span>
        </label>
        {hasChildren && isExpanded && (
          <div className="category-filter__children">
            {Array.from(node.children.values())
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(child => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  }, [isNodeSelected, isNodePartiallySelected, handleToggle, expandedNodes, toggleExpanded]);

  const selectedCount = selectedCategories.size;
  const totalCount = categories.length;
  const buttonLabel = allSelected
    ? "Categories: All"
    : selectedCount === 0
    ? "Categories: None"
    : `Categories: ${selectedCount}/${totalCount}`;

  // Create tooltip showing selected values
  const tooltipText = allSelected
    ? "All categories selected"
    : selectedCount === 0
    ? "No categories selected"
    : `Selected: ${Array.from(selectedCategories).sort().join(', ')}`;

  if (categories.length === 0) {
    return null; // Don't show if no categories
  }

  return (
    <div className="category-filter" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="category-filter__button"
        title={tooltipText}
      >
        {buttonLabel}
      </button>

      {isOpen && (
        <>
          <div className="category-filter__backdrop" onClick={() => setIsOpen(false)} />
          <div className="category-filter__menu">
            <div className="category-filter__header">
              <span>Log Categories</span>
              <div className="category-filter__actions">
                <button onClick={handleSelectAll} className="category-filter__action">
                  All
                </button>
                <button onClick={handleClearAll} className="category-filter__action">
                  None
                </button>
              </div>
            </div>
            <div className="category-filter__items">
              {Array.from(categoryTree.values())
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(node => renderNode(node, 0))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
